import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconPlus } from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../../components/common/Breadcrumb';
import { useSupportTickets, useTicketSummary, useTicketOptions } from '../../hooks/useSupportTickets';
import TicketTable from './components/TicketTable';
import { withoutEmpty, isForbidden, ITS_ACT_PERMISSIONS, ITS_AGENT_PERMISSIONS } from './utils';

// The Desk365-style tile row. `unassigned` is what the triage lead watches.
function SummaryTiles({ summary, isAgent, onSelect, active }) {
    if (!summary) return null;

    const tiles = [
        { key: 'unresolved', label: 'Unresolved', filter: { unresolved: true }, accent: 'text-amber-500' },
        { key: 'new', label: 'New', filter: { status: 'new' } },
        { key: 'open', label: 'Open', filter: { status: 'open' } },
        { key: 'pending', label: 'Pending', filter: { status: 'pending' } },
        { key: 'resolved', label: 'Resolved', filter: { status: 'resolved' } },
        ...(isAgent ? [
            { key: 'unassigned', label: 'Unassigned', filter: { unassigned: true } },
            { key: 'assigned_to_me', label: 'Assigned To Me', filter: { assigned_to: 'me' } },
        ] : []),
    ];

    return (
        <div className={`grid ${isAgent ? 'xl:grid-cols-7' : 'xl:grid-cols-5'} md:grid-cols-3 grid-cols-2 gap-15`}>
            {tiles.map(tile => {
                const isActive = active === tile.key;
                return (
                    <button
                        key={tile.key}
                        onClick={() => onSelect(isActive ? null : tile)}
                        className={`bg-bg-dark rounded-lg shadow-light p-15 text-left transition-colors border ${isActive ? 'border-primary' : 'border-transparent hover:border-border'}`}
                    >
                        <p className={`text-[22px]/[28px] font-semibold ${tile.accent || ''}`}>{summary[tile.key] ?? 0}</p>
                        <p className="text-xs text-text-secondary mt-3">{tile.label}</p>
                    </button>
                );
            })}
        </div>
    );
}

export default function SupportTickets() {
    const navigate = useNavigate();
    const { user, hasAnyPermission } = useAuth();
    const orgId = user?.orgId;

    const canAct = hasAnyPermission(ITS_ACT_PERMISSIONS);
    const isAgent = hasAnyPermission(ITS_AGENT_PERMISSIONS);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [tile, setTile] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(timer);
    }, [search]);

    const { data: options } = useTicketOptions();
    const { data: summary } = useTicketSummary(orgId);

    // A selected tile wins over the status dropdown filter.
    const filters = useMemo(() => withoutEmpty({
        search: debouncedSearch,
        priority: priorityFilter,
        request_type: typeFilter,
        ...(tile ? tile.filter : { status: statusFilter }),
    }), [debouncedSearch, priorityFilter, typeFilter, tile, statusFilter]);

    const { data: tickets = [], isLoading, error } = useSupportTickets(orgId, filters);

    const subHeaderComponent = (
        <div className="flex gap-5 mb-10 w-full justify-end flex-wrap">
            <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none"
            >
                <option value="">All types</option>
                {(options?.request_type || []).map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
            <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none"
            >
                <option value="">All priorities</option>
                <option value="P1,P2">P1 + P2</option>
                {(options?.requested_priority || []).map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
            <select
                value={tile ? '' : statusFilter}
                disabled={!!tile}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none disabled:opacity-60"
            >
                <option value="">All statuses</option>
                {(options?.status || []).map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
            <input
                type="text"
                placeholder="Search tickets..."
                className="bg-bg-light border border-border w-full max-w-[250px] py-[6px] px-3 min-h-[40px] rounded-[4px] placeholder:text-text-secondary focus:outline-none"
                value={search}
                onChange={e => setSearch(e.target.value)}
            />
        </div>
    );

    if (isForbidden(error)) {
        return (
            <div className="flex flex-col min-h-screen gap-20">
                <Breadcrumb title="IT Support" path="Support / My Tickets" />
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 text-center text-text-secondary">
                    You do not have access to IT support tickets.
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen gap-20">
            <Breadcrumb title="IT Support" path={isAgent ? 'Support / Tickets' : 'Support / My Tickets'} />

            <SummaryTiles
                summary={summary}
                isAgent={isAgent}
                active={tile?.key}
                onSelect={setTile}
            />

            <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                <div className="flex justify-between items-center mb-20 flex-wrap gap-10">
                    <h2 className="text-lg font-bold">
                        {isAgent ? 'All Tickets' : 'My Tickets'}
                        {tile && <span className="text-sm font-normal text-text-secondary ml-8">· {tile.label}</span>}
                    </h2>
                    {canAct && (
                        <button
                            onClick={() => navigate('/support/tickets/new')}
                            className="btn btn-primary flex items-center gap-5"
                        >
                            <IconPlus size={16} />
                            New Ticket
                        </button>
                    )}
                </div>

                <TicketTable
                    tickets={tickets}
                    isLoading={isLoading}
                    subHeaderComponent={subHeaderComponent}
                    showRequester={isAgent}
                    emptyMessage={
                        canAct
                            ? 'No tickets found. Click "New Ticket" to raise one.'
                            : 'No tickets found.'
                    }
                />
            </div>
        </div>
    );
}
